/**
 * F41 (Wave 2 Track D — admin) — tournament moderation reason dialogs.
 *
 * Mandatory-reason modals gated by the detail page on `tournaments:moderate`:
 *   - <SuspendTournamentDialog>     POST /admin/tournaments/:id/suspend
 *   - <CancelTournamentDialog>      POST /admin/tournaments/:id/cancel
 *   - <ForceRefundTournamentDialog> POST /admin/tournaments/:id/force-refund
 *   - <EditTournamentDialog>        PATCH /admin/tournaments/:id  (title/description)
 *   - <HideAnnouncementDialog>      POST /admin/tournament-announcements/:id/hide
 *
 * Each validates its reason against the matching `@bravemove/types` Zod schema
 * (1–500 chars). Composes F02 primitives only (Modal / FormField / Textarea /
 * Input / Button / Banner). Mirrors F40's SuspendPromotionDialog +
 * F32's SuspendOpenGameDialog reason-dialog pattern.
 */
import { useEffect, useMemo, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import { Banner, Button, FormField, Modal, Textarea, TextInput } from '@bravemove/ui-web';
import {
  CancelTournamentInputSchema,
  ForceRefundTournamentInputSchema,
  SuspendTournamentInputSchema,
  UpdateTournamentInputSchema,
} from '@bravemove/types';

// ─── Suspend ─────────────────────────────────────────────────────────────────

export interface SuspendTournamentDialogProps {
  readonly open: boolean;
  readonly onSuspend: (reason: string) => Promise<void>;
  readonly onClose: () => void;
  readonly errorMessage?: string | null;
}

export function SuspendTournamentDialog({
  open,
  onSuspend,
  onClose,
  errorMessage,
}: SuspendTournamentDialogProps) {
  const { t } = useI18n();
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!open) return;
    setReason('');
  }, [open]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const valid = useMemo(() => SuspendTournamentInputSchema.safeParse({ reason }).success, [reason]);

  const submit = async (): Promise<void> => {
    if (!valid) return;
    setSubmitting(true);
    try {
      await onSuspend(reason.trim());
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('admin:tournament.suspendDialog.title', {
        defaultValue: 'Suspend this tournament?',
      })}
      description={t('admin:tournament.suspendDialog.body', {
        defaultValue: 'It will be hidden from the marketplace. All data is retained.',
      })}
      size="md"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
            data-testid="suspend-tournament-cancel"
          >
            {t('admin:tournament.suspendDialog.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            variant="destructive"
            onClick={() => void submit()}
            loading={submitting}
            disabled={!valid || submitting}
            data-testid="suspend-tournament-submit"
          >
            {t('admin:tournament.suspendDialog.confirm', { defaultValue: 'Suspend' })}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3" data-testid="suspend-tournament-dialog">
        {errorMessage ? <Banner tone="danger">{errorMessage}</Banner> : null}
        <FormField
          label={t('admin:tournament.suspendDialog.reasonLabel', { defaultValue: 'Reason' })}
          required
        >
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('admin:tournament.suspendDialog.reasonPlaceholder', {
              defaultValue: 'Why are you suspending this tournament?',
            })}
            rows={4}
            maxLength={500}
            data-testid="suspend-tournament-reason"
          />
        </FormField>
      </div>
    </Modal>
  );
}

// ─── Publish (FIX 4.4, F8) ───────────────────────────────────────────────────

export interface PublishTournamentDialogProps {
  readonly open: boolean;
  readonly onPublish: (reason: string) => Promise<void>;
  readonly onClose: () => void;
  readonly errorMessage?: string | null;
}

/** Publish a draft on the organizer's behalf; the reason goes to the audit log. */
export function PublishTournamentDialog({
  open,
  onPublish,
  onClose,
  errorMessage,
}: PublishTournamentDialogProps) {
  const { t } = useI18n();
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!open) return;
    setReason('');
  }, [open]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const valid = reason.trim().length > 0 && reason.trim().length <= 500;

  const submit = async (): Promise<void> => {
    if (!valid) return;
    setSubmitting(true);
    try {
      await onPublish(reason.trim());
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('admin:tournament.publishDialog.title', {
        defaultValue: 'Publish this tournament?',
      })}
      description={t('admin:tournament.publishDialog.body', {
        defaultValue:
          'It goes live on the organizer’s behalf. The listing fee is waived and the plan limits don’t apply.',
      })}
      size="md"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
            data-testid="publish-tournament-cancel"
          >
            {t('admin:tournament.publishDialog.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            loading={submitting}
            disabled={!valid || submitting}
            data-testid="publish-tournament-submit"
          >
            {t('admin:tournament.publishDialog.confirm', { defaultValue: 'Publish' })}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3" data-testid="publish-tournament-dialog">
        {errorMessage ? <Banner tone="danger">{errorMessage}</Banner> : null}
        <FormField
          label={t('admin:tournament.publishDialog.reasonLabel', { defaultValue: 'Reason' })}
          required
        >
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('admin:tournament.publishDialog.reasonPlaceholder', {
              defaultValue: 'Why are you publishing it for them?',
            })}
            rows={3}
            maxLength={500}
            data-testid="publish-tournament-reason"
          />
        </FormField>
      </div>
    </Modal>
  );
}

// ─── Cancel ──────────────────────────────────────────────────────────────────

export interface CancelTournamentDialogProps {
  readonly open: boolean;
  readonly onCancel: (reason: string) => Promise<void>;
  readonly onClose: () => void;
  readonly errorMessage?: string | null;
}

export function CancelTournamentDialog({
  open,
  onCancel,
  onClose,
  errorMessage,
}: CancelTournamentDialogProps) {
  const { t } = useI18n();
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!open) return;
    setReason('');
  }, [open]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const valid = useMemo(() => CancelTournamentInputSchema.safeParse({ reason }).success, [reason]);

  const submit = async (): Promise<void> => {
    if (!valid) return;
    setSubmitting(true);
    try {
      await onCancel(reason.trim());
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('admin:tournament.cancelDialog.title', { defaultValue: 'Cancel this tournament?' })}
      description={t('admin:tournament.cancelDialog.body', {
        defaultValue:
          'All registrations are cancelled and flagged refund-eligible; all promos are reversed. This cannot be undone.',
      })}
      size="md"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
            data-testid="cancel-tournament-cancel"
          >
            {t('admin:tournament.cancelDialog.cancel', { defaultValue: 'Keep tournament' })}
          </Button>
          <Button
            variant="destructive"
            onClick={() => void submit()}
            loading={submitting}
            disabled={!valid || submitting}
            data-testid="cancel-tournament-submit"
          >
            {t('admin:tournament.cancelDialog.confirm', { defaultValue: 'Cancel tournament' })}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3" data-testid="cancel-tournament-dialog">
        {errorMessage ? <Banner tone="danger">{errorMessage}</Banner> : null}
        <FormField
          label={t('admin:tournament.cancelDialog.reasonLabel', { defaultValue: 'Reason' })}
          required
        >
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('admin:tournament.cancelDialog.reasonPlaceholder', {
              defaultValue: 'Why are you cancelling this tournament?',
            })}
            rows={4}
            maxLength={500}
            data-testid="cancel-tournament-reason"
          />
        </FormField>
      </div>
    </Modal>
  );
}

// ─── Force refund ────────────────────────────────────────────────────────────

export interface ForceRefundTournamentDialogProps {
  readonly open: boolean;
  readonly onForceRefund: (reason: string) => Promise<void>;
  readonly onClose: () => void;
  readonly errorMessage?: string | null;
}

export function ForceRefundTournamentDialog({
  open,
  onForceRefund,
  onClose,
  errorMessage,
}: ForceRefundTournamentDialogProps) {
  const { t } = useI18n();
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!open) return;
    setReason('');
  }, [open]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const valid = useMemo(
    () => ForceRefundTournamentInputSchema.safeParse({ reason }).success,
    [reason],
  );

  const submit = async (): Promise<void> => {
    if (!valid) return;
    setSubmitting(true);
    try {
      await onForceRefund(reason.trim());
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('admin:tournament.forceRefundDialog.title', {
        defaultValue: 'Force refund all paid registrations?',
      })}
      description={t('admin:tournament.forceRefundDialog.body', {
        defaultValue:
          'Every paid registration is flagged refund-eligible and marked refunded (money moves off-platform).',
      })}
      size="md"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
            data-testid="force-refund-cancel"
          >
            {t('admin:tournament.forceRefundDialog.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            variant="destructive"
            onClick={() => void submit()}
            loading={submitting}
            disabled={!valid || submitting}
            data-testid="force-refund-submit"
          >
            {t('admin:tournament.forceRefundDialog.confirm', { defaultValue: 'Force refund' })}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3" data-testid="force-refund-dialog">
        {errorMessage ? <Banner tone="danger">{errorMessage}</Banner> : null}
        <FormField
          label={t('admin:tournament.forceRefundDialog.reasonLabel', { defaultValue: 'Reason' })}
          required
        >
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('admin:tournament.forceRefundDialog.reasonPlaceholder', {
              defaultValue: 'Why are you forcing a refund?',
            })}
            rows={4}
            maxLength={500}
            data-testid="force-refund-reason"
          />
        </FormField>
      </div>
    </Modal>
  );
}

// ─── Edit (title + description ONLY — the standard organizer field-lock still
//     applies server-side; a true admin bypass is the post-MVP MN2 follow-up) ──

export interface EditTournamentDialogProps {
  readonly open: boolean;
  readonly initialTitle: string;
  readonly initialDescription: string | null;
  readonly onSave: (input: { title: string; description: string | null }) => Promise<void>;
  readonly onClose: () => void;
  readonly errorMessage?: string | null;
}

export function EditTournamentDialog({
  open,
  initialTitle,
  initialDescription,
  onSave,
  onClose,
  errorMessage,
}: EditTournamentDialogProps) {
  const { t } = useI18n();
  const [title, setTitle] = useState(initialTitle);
  const [description, setDescription] = useState(initialDescription ?? '');
  const [submitting, setSubmitting] = useState(false);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!open) return;
    setTitle(initialTitle);
    setDescription(initialDescription ?? '');
  }, [open, initialTitle, initialDescription]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const valid = useMemo(
    () =>
      UpdateTournamentInputSchema.safeParse({
        title,
        description: description.trim() === '' ? null : description,
      }).success,
    [title, description],
  );

  const submit = async (): Promise<void> => {
    if (!valid) return;
    setSubmitting(true);
    try {
      await onSave({
        title: title.trim(),
        description: description.trim() === '' ? null : description.trim(),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('admin:tournament.editDialog.title', { defaultValue: 'Edit tournament' })}
      description={t('admin:tournament.editDialog.body', {
        defaultValue:
          'Edits the title and description only — other fields follow the standard organizer field-lock.',
      })}
      size="md"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
            data-testid="edit-tournament-cancel"
          >
            {t('admin:tournament.editDialog.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            loading={submitting}
            disabled={!valid || submitting}
            data-testid="edit-tournament-submit"
          >
            {t('admin:tournament.editDialog.confirm', { defaultValue: 'Save changes' })}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3" data-testid="edit-tournament-dialog">
        {errorMessage ? <Banner tone="danger">{errorMessage}</Banner> : null}
        <FormField
          label={t('admin:tournament.editDialog.titleLabel', { defaultValue: 'Title' })}
          required
        >
          <TextInput
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={120}
            data-testid="edit-tournament-title"
          />
        </FormField>
        <FormField
          label={t('admin:tournament.editDialog.descriptionLabel', { defaultValue: 'Description' })}
        >
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            maxLength={2000}
            data-testid="edit-tournament-description"
          />
        </FormField>
      </div>
    </Modal>
  );
}

// ─── Hide an announcement (FIX 5.7, §10.8-F13) ───────────────────────────────

export interface HideAnnouncementDialogProps {
  readonly open: boolean;
  /** The announcement title, shown so the moderator knows which one. */
  readonly announcementTitle: string;
  readonly onHide: (reason: string) => Promise<void>;
  readonly onClose: () => void;
  readonly errorMessage?: string | null;
}

/**
 * Hide an organizer announcement from every reader
 * (`POST /admin/tournament-announcements/:id/hide`). The reason (1–500 chars)
 * is mandatory: it is what the audit row records.
 */
export function HideAnnouncementDialog({
  open,
  announcementTitle,
  onHide,
  onClose,
  errorMessage,
}: HideAnnouncementDialogProps) {
  const { t } = useI18n();
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!open) return;
    setReason('');
  }, [open]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const trimmed = reason.trim();
  const valid = trimmed.length >= 1 && trimmed.length <= 500;

  const submit = async (): Promise<void> => {
    if (!valid) return;
    setSubmitting(true);
    try {
      await onHide(trimmed);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('admin:tournament.hideAnnouncementDialog.title', {
        defaultValue: 'Hide this announcement?',
      })}
      description={t('admin:tournament.hideAnnouncementDialog.body', {
        defaultValue: 'Registrants and the public will no longer see it.',
      })}
      size="md"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
            data-testid="hide-announcement-cancel"
          >
            {t('admin:tournament.hideAnnouncementDialog.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            variant="destructive"
            onClick={() => void submit()}
            loading={submitting}
            disabled={!valid || submitting}
            data-testid="hide-announcement-submit"
          >
            {t('admin:tournament.hideAnnouncementDialog.confirm', { defaultValue: 'Hide' })}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3" data-testid="hide-announcement-dialog">
        {errorMessage ? <Banner tone="danger">{errorMessage}</Banner> : null}
        <p className="text-[13px] font-semibold text-ink">{announcementTitle}</p>
        <FormField
          label={t('admin:tournament.hideAnnouncementDialog.reasonLabel', {
            defaultValue: 'Reason',
          })}
          required
        >
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('admin:tournament.hideAnnouncementDialog.reasonPlaceholder', {
              defaultValue: 'Why are you hiding it?',
            })}
            rows={4}
            maxLength={500}
            data-testid="hide-announcement-reason"
          />
        </FormField>
      </div>
    </Modal>
  );
}
